export type LayoutMode = 'desktop' | 'tablet' | 'mobile';

export function getLayoutMode(width: number): LayoutMode {
  if (width < 780) return 'mobile';
  if (width < 1180) return 'tablet';
  return 'desktop';
}

export type ToolbarAction<Id extends string = string> = {
  id: Id;
  label: string;
  priority: number;
  pinned?: boolean;
  onClick: () => void;
};

export function splitToolbarActions<T extends ToolbarAction>(actions: T[], maxVisible: number) {
  const pinned = actions.filter((action) => action.pinned);
  const normal = actions.filter((action) => !action.pinned).sort((a, b) => b.priority - a.priority);
  const visible = normal.slice(0, Math.max(0, maxVisible));
  const overflow = normal.slice(Math.max(0, maxVisible));

  return { visible, overflow, pinned };
}

export function getMaxToolbarItems(width: number): number {
  if (width < 520) return 1;
  if (width < 760) return 2;
  if (width < 1040) return 3;
  return 5;
}

export function reorder<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) return items;
  if (fromIndex === toIndex) return items;

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export type ReorderState = {
  draggingId: string | null;
  overId: string | null;
};

export function moveById<T extends { id: string }>(items: T[], activeId: string, overId: string): T[] {
  const from = items.findIndex((item) => item.id === activeId);
  const to = items.findIndex((item) => item.id === overId);
  return reorder(items, from, to);
}

type SwipeConfig = {
  thresholdPx: number;
  maxOpenPx: number;
};

export function resolveSwipeState(deltaX: number, config: SwipeConfig): boolean {
  return deltaX < -config.thresholdPx;
}

export const defaultSwipeConfig: SwipeConfig = {
  thresholdPx: 64,
  maxOpenPx: 216
};

export type PresentationState =
  | { kind: 'none' }
  | { kind: 'task-details'; taskId: string }
  | { kind: 'edit-plan-block'; blockId: string }
  | { kind: 'confirm-delete-task'; taskId: string }
  | { kind: 'ai-explanation'; insightId: string }
  | { kind: 'new-document'; source: 'task' | 'plan' | 'session' }
  | { kind: 'settings' };

export const initialPresentation: PresentationState = { kind: 'none' };

export function isModalOpen(state: PresentationState): boolean {
  return state.kind !== 'none';
}

export function lazyInitialState<T>(factory: () => T): () => T {
  let cached: T | undefined;

  return () => {
    if (cached === undefined) cached = factory();
    return cached;
  };
}

export type AIInsightStatus = 'idle' | 'loading' | 'ready' | 'failed';

export type AIInsightCard = {
  id: string;
  title: string;
  body: string;
  status: AIInsightStatus;
  updatedAt?: string;
};

export function updateInsight(
  insights: AIInsightCard[],
  id: string,
  patch: Partial<AIInsightCard>
): AIInsightCard[] {
  return insights.map((card) => (card.id === id ? { ...card, ...patch, updatedAt: new Date().toISOString() } : card));
}
